import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import PropTypes from 'prop-types';
import { applyClasses, applyProps, useKeyPress, constants } from '../shared/js';
import { Button, Typography, Icons, HTML } from '..';

import styles from './tooltip.module.scss';
const { ESCAPE_KEY } = constants;

const Tooltip = ({ id, title, children, ...props }) => {
  const [open, setOpen] = useState(false);
  const [top, setTop] = useState(false);
  const [hide, setHide] = useState(true);
  const escPressed = useKeyPress(ESCAPE_KEY);
  const windowRef = useRef();

  const handleToggle = (e) => {
    e.preventDefault();
    setOpen(!open);
    setHide(false);
  };

  // move the tooltip to the top if it's off screen
  useEffect(() => {
    if (!open) return;

    const tooltipWindow = windowRef.current;
    const tooltipWindowBounds = tooltipWindow.getBoundingClientRect();
    const tooltipBottom = tooltipWindow.offsetHeight + tooltipWindowBounds.y;

    setTop(tooltipBottom > window.innerHeight);
  }, [open]);

  useEffect(() => {
    if (!open || !escPressed) return;
    setOpen(false);
  }, [escPressed, open]);

  const handleClose = () => {
    if (!open) return;
    setOpen(false);
  };

  const handleAnimationEnd = () => {
    if (open) return;
    setHide(true);
    setTop(false);
  };

  return (
    <>
      <OutsideClickHandler
        useCapture={false}
        onOutsideClick={handleClose}
        display="inline"
      >
        <div
          className={cn(styles.tooltip, applyClasses(props, styles), {
            [styles.open]: open,
          })}
        >
          <Button
            id={id}
            className={cn(
              styles.tooltipToggle,
              // eslint-disable-next-line react/prop-types
              props.className,
              { [styles.open]: open },
              applyClasses(props, styles)
            )}
            {...applyProps(props)}
            onClick={handleToggle}
          >
            {open ? (
              <>
                <HTML desktopOnly>
                  <div className={cn(styles.icon, styles.close)}>
                    <Icons.NavClose />
                  </div>
                </HTML>
                <HTML hideOnDesktop>
                  <div className={styles.icon}>
                    <Icons.Tooltip />
                  </div>
                </HTML>
              </>
            ) : (
              <div className={styles.icon}>
                <Icons.Tooltip />
              </div>
            )}
          </Button>
          {!hide && (
            <div
              ref={windowRef}
              className={cn(
                styles.window,
                // eslint-disable-next-line react/prop-types
                props.windowClassName,
                { [styles.open]: open, [styles.top]: top },
                applyClasses(props, styles)
              )}
              {...applyProps(props)}
              onAnimationEnd={handleAnimationEnd}
            >
              <div className={styles.title}>
                <Typography h5 left>
                  {title}
                </Typography>
              </div>
              <div className={styles.content}>{children}</div>
            </div>
          )}
        </div>
        <div
          className={cn(styles.window, styles.windowMobile, {
            [styles.open]: open,
          })}
          onAnimationEnd={handleAnimationEnd}
          onClick={handleClose}
        >
          <div
            className={styles.windowMobileInner}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeWindow}
              onClick={handleClose}
              type="button"
            >
              <Icons.Close />
            </button>
            <Typography h4 bold>
              {title}
            </Typography>
            <Typography className={styles.content}>{children}</Typography>
          </div>
        </div>
      </OutsideClickHandler>
    </>
  );
};

Tooltip.propTypes = {
  /** The ID of the tooltip button. Useful to select the tooltip during testing. */
  id: PropTypes.string,
  /** The title of the tooltip */
  title: PropTypes.string,
  /** The contents of the tooltip window */
  children: PropTypes.any,
  /** Applies primary styles to the tooltip toggle and window */
  primary: PropTypes.bool,
  /** Applies secondary styles to the tooltip toggle and window */
  secondary: PropTypes.bool,
};

Tooltip.defaultProps = {
  children: undefined,
  primary: undefined,
  secondary: undefined,
};

export default Tooltip;
